import { Info } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { JudgingCriterion } from '@/types';

// A criterion's name wherever the judges see it. When the admin gave it a description,
// hovering (or focusing) the name shows it, and a small icon says there is one.
export function CriterionName({
  criterion,
  className,
  children,
}: {
  criterion: JudgingCriterion;
  className?: string;
  // What to render as the name; defaults to the criterion's name.
  children?: ReactNode;
}) {
  const label = children ?? criterion.name;
  if (!criterion.description) return <span className={className}>{label}</span>;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          // Focusable, so the description is reachable from the keyboard too.
          tabIndex={0}
          className={cn(
            'inline-flex cursor-help items-center gap-1 underline decoration-dotted underline-offset-2',
            className,
          )}
        >
          {label}
          <Info className="h-3 w-3 shrink-0 opacity-60" aria-hidden />
        </span>
      </TooltipTrigger>
      <TooltipContent className="theme-ph26 max-w-xs whitespace-pre-line font-mono text-xs normal-case">
        {criterion.description}
      </TooltipContent>
    </Tooltip>
  );
}
